<div class="payment-integration-upsert-page">
  <!-- Loading State -->
  @if (isLoading()) {
    <app-payment-integration-upsert-skeleton />
  } @else {
    <!-- Page Header -->
    <div class="page-header">
      <div class="header-row">
        <div class="header-left">
          <p-button
            icon="pi pi-arrow-left"
            [text]="true"
            [rounded]="true"
            severity="secondary"
            pTooltip="Back to List"
            tooltipPosition="right"
            (onClick)="onCancel()"
          />
          <div class="title-section">
            <div class="title-row">
              <h1 class="page-title">
                @if (isEditMode) {
                  Edit:
                  {{ selectedPaymentMethodType() || "Payment Integration" }}
                } @else {
                  New Payment Integration
                }
              </h1>
              @if (!isProduction()) {
                <span
                  class="test-mode-badge"
                  (click)="testModePopover.toggle($event)"
                >
                  <i class="pi pi-info-circle"></i>
                  <span class="badge-text">Test Mode</span>
                </span>
                <p-popover #testModePopover>
                  <div class="popover-content">
                    <strong>Test Mode Required</strong>
                    <p>
                      Non-production sites can only create payment integrations
                      in test mode.
                    </p>
                  </div>
                </p-popover>
              }
            </div>
            @if (isEditMode) {
              <span class="entity-id">ID: {{ paymentIntegrationId() }}</span>
            }
          </div>
        </div>
        <div class="header-actions">
          @if (isEditMode) {
            <p-button
              label="Delete"
              icon="pi pi-trash"
              size="small"
              severity="danger"
              [outlined]="true"
              [loading]="isDeleting()"
              (onClick)="onDelete()"
              pTooltip="Delete"
              tooltipPosition="bottom"
              styleClass="icon-only-mobile"
            />
          }
          <p-button
            [label]="isEditMode ? 'Save' : 'Create'"
            [icon]="isEditMode ? 'pi pi-save' : 'pi pi-plus'"
            size="small"
            [loading]="isSaving()"
            [disabled]="form.invalid"
            (onClick)="onSubmit()"
            [pTooltip]="isEditMode ? 'Save' : 'Create'"
            tooltipPosition="bottom"
            styleClass="icon-only-mobile"
          />
        </div>
      </div>
    </div>

    <!-- Form Content -->
    <form [formGroup]="form" class="edit-form" autocomplete="off">
      <div class="form-content">
        <!-- Integration Details Section -->
        <div class="form-section">
          <div class="section-header">
            <i class="pi pi-credit-card"></i>
            <span>Integration Details</span>
          </div>
          <div class="section-body">
            <div class="form-grid">
              <!-- Payment Method Type -->
              <div class="form-field full-width">
                <label for="paymentMethodType" class="form-label">
                  Payment Method Type <span class="required">*</span>
                </label>
                <p-select
                  id="paymentMethodType"
                  formControlName="PaymentMethodType"
                  [options]="paymentMethodOptions"
                  placeholder="Select a payment method"
                  [showClear]="!!form.controls.PaymentMethodType.value"
                  [filter]="true"
                  filterPlaceholder="Search..."
                  styleClass="w-full"
                  [disabled]="isEditMode"
                  appendTo="body"
                />
                @if (
                  form.controls.PaymentMethodType.invalid &&
                  form.controls.PaymentMethodType.touched
                ) {
                  <small class="error-message"
                    >Payment method type is required</small
                  >
                }
              </div>

              <!-- Dynamic Fields based on Payment Method Type -->
              @if (selectedPaymentMethodType()) {
                <!-- API Account -->
                @if (apiAccountLabel) {
                  <div class="form-field">
                    <label for="apiAccount" class="form-label">
                      {{ apiAccountLabel }}
                    </label>
                    <input
                      id="apiAccount"
                      type="text"
                      pInputText
                      formControlName="ApiAccount"
                      [placeholder]="apiAccountPlaceholder"
                      autocomplete="off"
                      data-lpignore="true"
                      data-form-type="other"
                    />
                  </div>
                }

                <!-- API Key 1 -->
                @if (apiKey1Label) {
                  <div class="form-field">
                    <label for="apiKey1" class="form-label">
                      {{ apiKey1Label }}
                    </label>
                    <p-password
                      id="apiKey1"
                      formControlName="ApiKey1"
                      [toggleMask]="true"
                      [feedback]="false"
                      [placeholder]="apiKey1Placeholder"
                      autocomplete="off"
                      styleClass="w-full"
                      inputStyleClass="w-full"
                    />
                  </div>
                }

                <!-- API Key 2 -->
                @if (apiKey2Label) {
                  <div class="form-field">
                    <label for="apiKey2" class="form-label">
                      {{ apiKey2Label }}
                    </label>
                    <p-password
                      id="apiKey2"
                      formControlName="ApiKey2"
                      [toggleMask]="true"
                      [feedback]="false"
                      [placeholder]="apiKey2Placeholder"
                      autocomplete="off"
                      styleClass="w-full"
                      inputStyleClass="w-full"
                    />
                  </div>
                }

                <!-- API Key 3 -->
                @if (apiKey3Label) {
                  <div class="form-field">
                    <label for="apiKey3" class="form-label">
                      {{ apiKey3Label }}
                    </label>
                    <p-password
                      id="apiKey3"
                      formControlName="ApiKey3"
                      [toggleMask]="true"
                      [feedback]="false"
                      [placeholder]="apiKey3Placeholder"
                      autocomplete="off"
                      styleClass="w-full"
                      inputStyleClass="w-full"
                    />
                  </div>
                }

                <!-- Checkboxes inline -->
                @if (showTestModeCheckbox || showAuthorizeOnlyCheckbox) {
                  <div class="form-field checkbox-row full-width">
                    @if (showTestModeCheckbox) {
                      <div class="checkbox-item">
                        <p-checkbox
                          formControlName="TestMode"
                          [binary]="true"
                          inputId="testMode"
                          [pTooltip]="testModeTooltip"
                          tooltipPosition="top"
                        />
                        <label for="testMode" class="checkbox-label">
                          Test Mode
                          @if (testModeDisabled) {
                            <span class="required-tag">(Required)</span>
                          }
                        </label>
                      </div>
                    }
                    @if (showAuthorizeOnlyCheckbox) {
                      <div class="checkbox-item">
                        <p-checkbox
                          formControlName="AuthorizeOnly"
                          [binary]="true"
                          inputId="authorizeOnly"
                          pTooltip="Authorize transactions only, do not capture"
                          tooltipPosition="top"
                        />
                        <label for="authorizeOnly" class="checkbox-label">
                          Authorize Only
                        </label>
                      </div>
                    }
                  </div>
                }
              }
            </div>
          </div>
        </div>
      </div>
    </form>
  }
</div>

<!-- Toast Messages - only render in embedded mode since main app has global toast -->
@if (embeddedMode) {
  <p-toast
    position="top-right"
    [breakpoints]="{ '920px': { width: '100%', right: '0', left: '0' } }"
  />
}

<!-- Confirmation Dialog -->
<p-confirmDialog
  [style]="{ width: '450px', maxWidth: '90vw' }"
  [baseZIndex]="10000"
  rejectButtonStyleClass="p-button-text"
/>

<!-- Subscription Upgrade Dialog -->
<app-subscription-upgrade-dialog
  [visible]="showUpgradeDialog()"
  [featureName]="'a payment integration of type ' + selectedPaymentMethodType()"
  [tierName]="currentTierName()"
  [upgradeUrl]="upgradeUrl()"
  (visibleChange)="onUpgradeDialogVisibleChange($event)"
/>
